/**
 * Security card container (D-133 rows): own PIN change (`POST /security/pin/change`, D-018), PIN /
 * access reset for others (`/security/pin/reset`, `/users/reset-access`: manage_users + own PIN, D-037),
 * 2FA status and re-enrolment (`/security/totp/enrol` own PIN → `/security/totp/confirm`, D-036),
 * idle timeout (`PUT /settings security.idle_timeout_minutes`, D-039), device approval (D-040,
 * Super Manager only), working hours (read-only line, managed per user, D-041).
 * Office settings need the `settings` token to read and the card token + own PIN to write (D-123).
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SecurityCard } from "@faylasuf/ui";
import type { EnrolmentData, PinProof } from "../../api/types.js";
import { useApp, useSession } from "../../app/context.js";
import { errorMessage, isSessionLost } from "../../app/errors.js";
import { usePinGate } from "../../app/pin-gate.js";
import { keys } from "../../app/query.js";
import { useToast } from "../../app/toast.js";
import { EnrolDialog, LinkDialog, setupLinkFor } from "./dialogs.js";

const PIN_RE = /^\d{6}$/;

export function SecurityContainer() {
  const { source, t } = useApp();
  const { session, hasPermission, isSuperManager, refresh, sessionLost } = useSession();
  const toast = useToast();
  const qc = useQueryClient();
  const gate = usePinGate();
  const fail = (e: unknown) => (isSessionLost(e) ? sessionLost() : toast(errorMessage(t, e), "error"));

  const canManageUsers = hasPermission("manage_users");
  const canReadSettings = hasPermission("settings");
  const canWriteSecurity = canReadSettings && hasPermission("security_codes");
  const settings = useQuery({ queryKey: keys.settings, queryFn: () => source.settings(), enabled: canReadSettings });
  const users = useQuery({ queryKey: keys.users, queryFn: () => source.users(), enabled: canManageUsers });
  const setting = (key: string): unknown => settings.data?.[key]?.value;

  // --- [C35] D-049 e-mail for alerts (own address, own PIN, confirmed by a code) ---
  const email = useQuery({ queryKey: ["security-email"], queryFn: () => source.securityEmail() });
  const [emailInput, setEmailInput] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const emailRun = async (fn: () => Promise<unknown>, done: string) => {
    setEmailBusy(true);
    try {
      const r = await fn();
      if (r !== undefined) {
        toast(t(done));
        await qc.invalidateQueries({ queryKey: ["security-email"] });
      }
    } catch (e) {
      fail(e);
    } finally {
      setEmailBusy(false);
    }
  };
  const saveEmail = () => emailRun(() => gate.withPin((proof: PinProof) => source.setSecurityEmail(emailInput.trim(), proof.pin ?? ""), { message: t("pin.message_email") }), "security.email_code_sent").then(() => setEmailCode(""));
  const verifyEmail = () => emailRun(() => source.verifySecurityEmail(emailCode), "security.email_done").then(() => setEmailInput(""));
  const removeEmail = () => emailRun(() => gate.withPin((proof: PinProof) => source.removeSecurityEmail(proof.pin ?? ""), { message: t("pin.message_email") }), "security.email_removed");

  // --- change own PIN ---
  const [cp, setCp] = useState({ currentPin: "", newPin: "", confirmPin: "" });
  const changePin = useMutation({
    mutationFn: () => source.changePin(cp.currentPin, cp.newPin),
    onSuccess: () => {
      setCp({ currentPin: "", newPin: "", confirmPin: "" });
      toast(t("toast.pin_changed"));
      void refresh();
    },
    onError: fail,
  });
  const savePin = () => {
    if (!PIN_RE.test(cp.currentPin) || !PIN_RE.test(cp.newPin)) return toast(t("security.pin_format"), "error");
    if (cp.newPin !== cp.confirmPin) return toast(t("security.pin_mismatch"), "error");
    changePin.mutate();
  };

  // --- reset others (D-037) ---
  const [selectedUserId, setSelectedUserId] = useState("");
  const [otherPin, setOtherPin] = useState("");
  const [link, setLink] = useState<string | null>(null);
  const [othersBusy, setOthersBusy] = useState(false);
  const selectedUser = users.data?.find((u) => u.id === selectedUserId);
  const resetPin = async () => {
    if (!selectedUser) return toast(t("security.select_user_first"), "error");
    if (!PIN_RE.test(otherPin)) return toast(t("security.pin_format"), "error");
    setOthersBusy(true);
    try {
      const r = await gate.withPin((proof: PinProof) => source.resetPin(selectedUser.id, otherPin, proof.pin ?? ""), { message: t("pin.message_reset_pin", { user: selectedUser.username }) });
      if (r !== undefined) {
        setOtherPin("");
        toast(t("toast.pin_reset"));
      }
    } catch (e) {
      fail(e);
    } finally {
      setOthersBusy(false);
    }
  };
  const resetAccess = async () => {
    if (!selectedUser) return toast(t("security.select_user_first"), "error");
    setOthersBusy(true);
    try {
      const r = await gate.withPin((proof: PinProof) => source.resetAccess(selectedUser.id, proof.pin ?? ""), { message: t("pin.message_reset_access", { user: selectedUser.username }) });
      if (r?.setupToken) setLink(setupLinkFor(r.setupToken));
      void qc.invalidateQueries({ queryKey: keys.users });
    } catch (e) {
      fail(e);
    } finally {
      setOthersBusy(false);
    }
  };

  // --- 2FA re-enrolment (D-036) ---
  const [enrol, setEnrol] = useState<{ data: EnrolmentData | null; pin: string; code: string; codes: readonly string[] | null; error: string | null; busy: boolean } | null>(null);
  const startEnrol = async () => {
    try {
      const r = await gate.withPin(async (proof: PinProof) => ({ data: await source.securityTotpEnrol(proof.pin ?? ""), pin: proof.pin ?? "" }), { message: t("pin.message_enrol") });
      if (r) setEnrol({ data: r.data, pin: r.pin, code: "", codes: null, error: null, busy: false });
    } catch (e) {
      fail(e);
    }
  };
  const confirmEnrol = async () => {
    if (!enrol) return;
    setEnrol({ ...enrol, busy: true, error: null });
    try {
      const r = await source.securityTotpConfirm(enrol.code, enrol.pin);
      setEnrol({ ...enrol, busy: false, codes: r.recoveryCodes });
      toast(t("toast.totp_enrolled"));
      void refresh();
    } catch (e) {
      if (isSessionLost(e)) return sessionLost();
      setEnrol({ ...enrol, busy: false, error: errorMessage(t, e) });
    }
  };

  // --- office settings of the card (D-123: card token + own PIN) ---
  const [idleMinutes, setIdleMinutes] = useState<number | null>(null);
  const idleValue = idleMinutes ?? (typeof setting("security.idle_timeout_minutes") === "number" ? (setting("security.idle_timeout_minutes") as number) : session.session.idleMinutes);
  const setSetting = async (key: string, value: unknown, after?: () => void) => {
    try {
      const r = await gate.withPin((proof: PinProof) => source.setSetting(key, value, proof.pin ?? ""), { message: t("pin.message_setting", { key }) });
      if (r !== undefined) {
        toast(t("toast.saved"));
        after?.();
        void qc.invalidateQueries({ queryKey: keys.settings });
        void refresh();
      }
    } catch (e) {
      fail(e);
    }
  };

  return (
    <>
      <SecurityCard
        changePin={{ ...cp, onChange: (field, value) => setCp({ ...cp, [field]: value.replace(/\D/g, "").slice(0, 6) }), onSave: savePin, busy: changePin.isPending }}
        resetOthers={
          canManageUsers
            ? {
                users: (users.data ?? []).filter((u) => u.id !== session.user.id).map((u) => ({ id: u.id, label: `${u.username} — ${u.displayName}` })),
                selectedUserId,
                onSelectUser: setSelectedUserId,
                newPin: otherPin,
                onNewPinChange: (v) => setOtherPin(v.replace(/\D/g, "").slice(0, 6)),
                onResetPin: () => void resetPin(),
                onResetAccess: () => void resetAccess(),
                busy: othersBusy,
              }
            : null
        }
        twoFactor={{
          enrolled: session.user.twoFactorEnrolled,
          requiredForAll: setting("security.two_factor_required_for_all") !== false,
          ...(isSuperManager && canWriteSecurity ? { onRequiredForAllChange: (v: boolean) => void setSetting("security.two_factor_required_for_all", v) } : {}),
          onReEnrol: () => void startEnrol(),
        }}
        idleTimeout={{
          minutes: idleValue,
          onChange: (m) => setIdleMinutes(Number.isFinite(m) ? m : 0),
          onSave: () => void setSetting("security.idle_timeout_minutes", idleValue, () => setIdleMinutes(null)),
          editable: canWriteSecurity,
        }}
        deviceApproval={{
          enabled: setting("security.approved_devices_only") === true,
          ...(isSuperManager && canWriteSecurity ? { onChange: (v: boolean) => void setSetting("security.approved_devices_only", v) } : {}),
        }}
        email={{
          address: email.data?.address ?? null,
          verified: email.data?.verified ?? false,
          configured: email.data?.mailConfigured ?? false,
          input: emailInput,
          onInput: setEmailInput,
          code: emailCode,
          onCode: setEmailCode,
          awaitingCode: email.data ? Boolean(email.data.address) && !email.data.verified : false,
          onSave: () => void saveEmail(),
          onVerify: () => void verifyEmail(),
          onRemove: () => void removeEmail(),
          busy: emailBusy,
        }}
        workingHours={{}}
      />
      {gate.dialog}
      {enrol && (
        <EnrolDialog
          enrolment={enrol.data}
          code={enrol.code}
          onCodeChange={(code) => setEnrol({ ...enrol, code })}
          recoveryCodes={enrol.codes}
          error={enrol.error}
          busy={enrol.busy}
          onConfirm={() => void confirmEnrol()}
          onClose={() => setEnrol(null)}
        />
      )}
      {link && <LinkDialog title={t("security.reset_access")} link={link} onClose={() => setLink(null)} />}
    </>
  );
}
